import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import { useControlledState } from 'ui-components/hooks/use-controlled-component-state/use-controlled-component-state';

import { createColumnDefinitionBuilder } from '../../../../../components/items-table/create-column-definition-builder';

import messages from '../messages';
import ProductTypeAheadInput from '../../../../../components/product-type-ahead-input/product-type-ahead-input';
import style from '../style.module.less';

const EditItemSkuColumnId = 'edit-sku';
const SkuPropertyName = 'sku';

const editItemSkuDataBuilder = (item) => {
  return {
    id: item.id,
    sku: item.sku,
    readonly: item.readonly,
  };
};

const EditItemSku = ({ value: item, onColumnBlur, disabled = false }) => {
  const intl = useIntl();
  const [sku, setSku] = useControlledState(item.sku);

  const onBlur = useCallback(
    (event) => {
      onColumnBlur(item.id, {
        [SkuPropertyName]: event?.target?.value,
      });
    },
    [onColumnBlur, item.id],
  );

  const onResultSelected = useCallback(
    (product) => {
      onColumnBlur(item.id, {
        name: product.name,
        sku: product.sku,
        productId: product.productId,
        productThumbnailUrl: product.thumbnailUrl,
        dangerousGoods: product.dangerousGoods,
        // unitPrice: product.unit, // TODO: have to fix currency input first
      });
    },
    [onColumnBlur, item.id],
  );

  const onChange = useCallback(
    (value) => {
      setSku(value);
    },
    [setSku],
  );

  if (item.readonly?.code) {
    return <div aria-labelledby={EditItemSkuColumnId}>{sku}</div>;
  }

  return (
    <ProductTypeAheadInput
      ariaLabelledBy={EditItemSkuColumnId}
      className={style['sku-input']}
      disabled={disabled}
      name={intl.formatMessage(messages.itemNameInputPlaceholder)}
      onBlur={onBlur}
      onChange={onChange}
      onResultSelected={onResultSelected}
      placeholder={intl.formatMessage(messages.itemNameInputPlaceholder)}
      value={sku}
    />
  );
};

EditItemSku.propTypes = {
  value: PropTypes.shape({
    id: PropTypes.string.isRequired,
    sku: PropTypes.string,
    readonly: PropTypes.shape({
      code: PropTypes.string,
    }),
  }).isRequired,
  onColumnBlur: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const createEditItemSkuColumnDefinition = createColumnDefinitionBuilder({
  header: <FormattedMessage {...messages.itemSku} />,
  accessor: EditItemSkuColumnId,
  cell: EditItemSku,
  width: 1.5,
});

export { EditItemSkuColumnId, editItemSkuDataBuilder, createEditItemSkuColumnDefinition };
